import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Button,
  CircularProgress,
  LinearProgress,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Skeleton
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoIcon from '@mui/icons-material/Info';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import QueueMusicIcon from '@mui/icons-material/QueueMusic';
import therapistApi from '../../services/therapistApi';
import { formatDistanceToNow } from 'date-fns';

const AudioExportQueue = () => {
  const [exports, setExports] = useState([]);
  const [stats, setStats] = useState({
    pending: 0,
    processing: 0,
    completed: 0,
    failed: 0
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalExports, setTotalExports] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMenuJob, setSelectedMenuJob] = useState(null);
  const [error, setError] = useState(null);

  const fetchExports = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError(null);
    
    try {
      const response = await therapistApi.getAudioExports({
        page: page + 1,
        limit: rowsPerPage
      });

      if (response.data) {
        setExports(response.data.exports || []);
        setStats(response.data.stats || {
          pending: 0,
          processing: 0,
          completed: 0,
          failed: 0
        });
        setTotalExports(response.data.total || 0);
      }
    } catch (err) {
      console.error('Error fetching audio exports:', err);
      setError('Failed to load audio exports');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchExports();
  }, [page, rowsPerPage]);

  // Auto-refresh for pending/processing jobs
  useEffect(() => {
    const hasActiveJobs = stats.pending > 0 || stats.processing > 0;
    if (hasActiveJobs) {
      const interval = setInterval(() => {
        fetchExports(false);
      }, 10000); // Refresh every 10 seconds
      return () => clearInterval(interval);
    }
  }, [stats.pending, stats.processing]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchExports();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'default';
      case 'processing': return 'primary';
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'cancelled': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <HourglassEmptyIcon fontSize="small" />;
      case 'processing': return <CircularProgress size={16} />;
      case 'completed': return <CheckCircleIcon fontSize="small" />;
      case 'failed': return <ErrorIcon fontSize="small" />;
      case 'cancelled': return <CancelIcon fontSize="small" />;
      default: return null;
    }
  };

  const handleDownload = async (job) => {
    try {
      // Check if we have a direct download URL (signed URL from Cloud Storage)
      if (job.fileUrl) {
        // Use the signed URL directly - no authentication needed
        window.open(job.fileUrl, '_blank');
      } else {
        // Fallback to API endpoint for local development
        const token = localStorage.getItem('token');
        const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
        
        // Create a download link with authentication
        const downloadUrl = `${baseUrl}/therapist/audio-exports/${job.id}/download?token=${token}`;
        
        // Create a temporary link and click it to trigger download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `export-${job.clientCode}-${job.id}.mp3`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
    } catch (err) {
      console.error('Error downloading file:', err);
      setError('Failed to download file');
    }
  };

  const handleDelete = async () => {
    if (!jobToDelete) return;
    
    try {
      await therapistApi.deleteAudioExport(jobToDelete.id);
      setDeleteConfirmOpen(false);
      setJobToDelete(null);
      fetchExports();
    } catch (err) {
      console.error('Error deleting export:', err);
      setError('Failed to delete export');
    }
  };

  const handleRetry = async (job) => {
    try {
      await therapistApi.retryAudioExport(job.id);
      fetchExports();
    } catch (err) {
      console.error('Error retrying export:', err);
      setError(err.response?.data?.reason || 'Failed to retry export');
    }
  };

  const handleViewDetails = async (job) => {
    try {
      const response = await therapistApi.getAudioExportDetails(job.id);
      setSelectedJob(response.data.export);
      setDetailsDialogOpen(true);
    } catch (err) {
      console.error('Error fetching job details:', err);
      setError('Failed to load export details');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  if (loading && exports.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={100} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Audio Export Queue</Typography>
        <Button
          startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
          onClick={handleRefresh}
          disabled={refreshing}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Status Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Pending
              </Typography>
              <Typography variant="h4">
                {stats.pending}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Processing
              </Typography>
              <Typography variant="h4" color="primary">
                {stats.processing}
              </Typography>
              {stats.processing > 0 && (
                <LinearProgress sx={{ mt: 1 }} />
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Ready
              </Typography>
              <Typography variant="h4" color="success.main">
                {stats.completed}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Failed
              </Typography>
              <Typography variant="h4" color="error.main">
                {stats.failed}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Export Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Client</TableCell>
              <TableCell>Conversation Date</TableCell>
              <TableCell>Requested</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Progress</TableCell>
              <TableCell>Size</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {exports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No audio exports found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              exports.map((job) => (
                <TableRow key={job.id}>
                  <TableCell>{job.clientCode}</TableCell>
                  <TableCell>
                    {job.conversationDate 
                      ? new Date(job.conversationDate).toLocaleDateString()
                      : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {formatDistanceToNow(new Date(job.requestedAt), { addSuffix: true })}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={job.status}
                      color={getStatusColor(job.status)}
                      size="small"
                      icon={getStatusIcon(job.status)}
                    />
                  </TableCell>
                  <TableCell>
                    {job.status === 'processing' ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LinearProgress
                          variant="determinate"
                          value={job.progress}
                          sx={{ width: 100, mr: 1 }}
                        />
                        <Typography variant="caption">
                          {job.progress}%
                        </Typography>
                      </Box>
                    ) : job.status === 'completed' ? (
                      <Typography color="success.main" variant="body2">
                        Ready
                      </Typography>
                    ) : job.status === 'failed' && job.error ? (
                      <Tooltip title={job.error.message}>
                        <Typography color="error" variant="caption">
                          {job.error.type}
                        </Typography>
                      </Tooltip>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {job.fileSize ? formatFileSize(job.fileSize) : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                      {job.status === 'completed' && (
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(job)}
                            color="primary"
                            aria-label="Download"
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {job.status === 'failed' && job.canRetry && (
                        <Tooltip title="Retry">
                          <IconButton
                            size="small"
                            onClick={() => handleRetry(job)}
                            color="warning"
                            aria-label="Retry"
                          >
                            <RefreshIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {(job.status === 'pending' || job.status === 'completed' || job.status === 'failed') && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setJobToDelete(job);
                              setDeleteConfirmOpen(true);
                            }}
                            color="error"
                            aria-label="Delete"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(job)}
                          aria-label="View details"
                        >
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalExports}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Export Details</DialogTitle>
        <DialogContent>
          {selectedJob && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Client Code:</strong> {selectedJob.clientCode}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Status:</strong> {selectedJob.status}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Messages:</strong> {selectedJob.messageCount}
              </Typography>
              {selectedJob.duration && (
                <Typography variant="body2" gutterBottom>
                  <strong>Duration:</strong> {selectedJob.duration}
                </Typography>
              )}
              {selectedJob.fileSize && (
                <Typography variant="body2" gutterBottom>
                  <strong>File Size:</strong> {formatFileSize(selectedJob.fileSize)}
                </Typography>
              )}
              {selectedJob.downloadCount > 0 && (
                <Typography variant="body2" gutterBottom>
                  <strong>Downloads:</strong> {selectedJob.downloadCount}
                </Typography>
              )}
              {selectedJob.expiresAt && (
                <Typography variant="body2" gutterBottom>
                  <strong>Expires:</strong> {new Date(selectedJob.expiresAt).toLocaleDateString()}
                </Typography>
              )}
              {selectedJob.error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Error:</strong> {selectedJob.error.message}
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this audio export for client {jobToDelete?.clientCode}?
            {jobToDelete?.status === 'completed' && ' The audio file will be permanently removed.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AudioExportQueue;